import { CONFIG } from "../config";
import { DEMO_CV, SUGGESTS, SUGGESTS_INDEPENDANTS } from "../content/copy";
import { messageSecondDiagnostic } from "../content/secondDiagnostic";
import { useApp } from "../state/AppState";
import { Fil } from "../components/Fil";

export type Mode1 = "job" | "cv";

interface Props {
  mode: Mode1;
  setMode: (mode: Mode1) => void;
  job: string;
  setJob: (value: string) => void;
  cv: string;
  setCv: (value: string) => void;
  erreur: string | null;
  jobInvalide: boolean;
  onGoJob: () => void;
  onGoCv: () => void;
}

/** Volet 01 — saisie : métier en champ libre, ou CV collé. */
export function Volet1Start({
  mode,
  setMode,
  job,
  setJob,
  cv,
  setCv,
  erreur,
  jobInvalide,
  onGoJob,
  onGoCv,
}: Props) {
  const { goTo, modeCv, parrainage, aSonCompte } = useApp();
  const n = cv.length;
  /*
    Ce que le serveur sait, dit avant la saisie et non après — 31/08/2026.

    La limite des quatre-vingt-dix jours était déjà appliquée, et bien dite,
    mais seulement une fois l'analyse demandée. En mode CV, cela voulait dire
    coller son curriculum en entier pour apprendre ensuite que le diagnostic
    était fermé. Le même texte, servi ici, fait porter l'attente au bon moment.

    `second` est facultatif : un serveur qui ne le rend pas laisse
    `messageSecond` à `null` et l'écran se comporte comme avant. Ne pas savoir
    n'est pas savoir que c'est fermé.
  */
  const messageSecond = parrainage?.second ? messageSecondDiagnostic(parrainage.second) : null;
  /*
    Le mode CV ne s'affiche que quand on **sait** qu'il est ouvert.

    Tant que le serveur n'a pas répondu, l'onglet n'apparaît pas et le mode
    métier occupe l'écran seul — il est de toute façon celui qui est
    sélectionné par défaut. Rien ne saute donc sous le doigt de personne : un
    second onglet arrive, ou n'arrive pas.
  */
  const cvOuvert = modeCv === true;

  return (
    <section>
      <div className="shell narrow app">
        <Fil etape={1} />
        <h2>Votre exposition</h2>

        {messageSecond !== null && (
          <div className="notice" role="status">
            {messageSecond}
          </div>
        )}
        <p className="sub">
          {cvOuvert
            ? "Donnez votre métier, ou collez votre CV pour un résultat plus fin."
            : "Donnez votre métier. Les tâches types vous sont proposées ; vous cochez les vôtres."}
        </p>

        {cvOuvert && (
          <div className="tabs" role="tablist">
            <button
              type="button"
              role="tab"
              id="tab-job"
              aria-selected={mode === "job"}
              aria-controls="pane-job"
              onClick={() => setMode("job")}
            >
              Mon métier · 30 s
            </button>
            <button
              type="button"
              role="tab"
              id="tab-cv"
              aria-selected={mode === "cv"}
              aria-controls="pane-cv"
              onClick={() => setMode("cv")}
            >
              Mon CV · précis
            </button>
          </div>
        )}

        {mode === "job" || !cvOuvert ? (
          <div id="pane-job" role="tabpanel" aria-labelledby="tab-job">
            <div className="field">
              {/* Le statut déclaré à l'accueil tient jusqu'ici : l'accueil disait
                  « Votre activité » et proposait des indépendants, cet écran
                  remontrait la liste salariée. Relecture du 06/09/2026. */}
              <label htmlFor="job">{aSonCompte ? "Votre activité" : "Votre métier"}</label>
              <input
                type="text"
                id="job"
                placeholder={aSonCompte ? "ex. graphiste, consultant…" : "ex. sage-femme, agent immobilier…"}
                value={job}
                onChange={(e) => setJob(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onGoJob();
                }}
                style={jobInvalide ? { borderColor: "var(--high)" } : undefined}
                aria-invalid={jobInvalide}
                aria-describedby={jobInvalide ? "job-erreur" : undefined}
              />
              {jobInvalide && (
                <p id="job-erreur" className="fine" role="alert" style={{ color: "var(--high)", margin: "6px 0 0" }}>
                  Indiquez votre métier, trois lettres au moins.
                </p>
              )}
              <div className="suggests">
                {(aSonCompte ? SUGGESTS_INDEPENDANTS : SUGGESTS).map((s) => (
                  <button key={s} type="button" onClick={() => setJob(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <button type="button" className="btn" onClick={onGoJob}>
              Établir mes tâches clés
            </button>
            <p className="hint">
              Les tâches types de votre métier vous sont proposées. Vous cochez les vôtres.
            </p>
          </div>
        ) : (
          <div id="pane-cv" role="tabpanel" aria-labelledby="tab-cv">
            <div className="field">
              <label htmlFor="cv">Votre CV ou la description de votre poste</label>
              <textarea
                id="cv"
                placeholder="Missions, tâches quotidiennes, outils utilisés…"
                value={cv}
                onChange={(e) => setCv(e.target.value)}
                aria-describedby="count"
              />
              <div className="counter" id="count">
                {n} caractère{n > 1 ? "s" : ""}
                {n > CONFIG.cvMaxChars
                  ? ` — seuls les ${CONFIG.cvMaxChars.toLocaleString("fr-FR")} premiers seront analysés`
                  : ""}
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ marginTop: "10px" }}
                onClick={() => setCv(DEMO_CV)}
              >
                Utiliser un exemple
              </button>
            </div>
            <button type="button" className="btn" onClick={onGoCv}>
              Analyser mon CV
            </button>
            <p className="hint">Analysé à la demande, jamais conservé.</p>
          </div>
        )}

        {erreur !== null && (
          <div className="err" role="alert">
            {erreur}
          </div>
        )}

        <button type="button" className="back" onClick={() => goTo("home")}>
          ← Revenir au diagnostic
        </button>
      </div>
    </section>
  );
}
